/**
 * Customer Utilities - Helper functions for customer data processing
 * Provides quality filtering, activity calculation, and status determination
 */

import { format } from "date-fns";
import { logError } from "@/shared/utils/logger.js";

/**
 * Resolve the stable identifier used by the customer profile route.
 * Some API/search payloads use id/_id while the full customer model uses customerId.
 * @param {Object} customer - Customer object
 * @returns {string|null} Customer profile identifier
 */
export const getCustomerProfileId = (customer) => {
  if (!customer) return null;
  return customer.customerId || customer.id || customer._id || null;
};

/**
 * Build the customer profile route for a customer record.
 * @param {Object} customer - Customer object
 * @returns {string|null} Customer profile path
 */
export const getCustomerProfilePath = (customer) => {
  const customerId = getCustomerProfileId(customer);
  if (!customerId) return null;
  return `/customers/${encodeURIComponent(String(customerId))}`;
};

/**
 * Check if customer is low quality (should be filtered)
 * Filters out customers with placeholder phones or "N/A" names
 * @param {Object} customer - Customer object
 * @returns {boolean} True if customer is low quality
 */
export const isLowQualityCustomer = (customer) => {
  if (!customer) return true;

  // Filter out customers with placeholder phones
  if (!customer.phone || customer.phone.startsWith("TEMP_")) {
    return true;
  }

  // Filter out customers with "N/A" as name or empty name
  if (
    customer.name === "N/A" ||
    !customer.name ||
    customer.name.trim() === ""
  ) {
    return true;
  }

  return false;
};

/**
 * Calculate last activity date from customer's updatedAt
 * Since list API doesn't return full relations, we use updatedAt as proxy
 * @param {Object} customer - Customer object
 * @returns {Date|null} Last activity date
 */
export const calculateLastActivityDate = (customer) => {
  if (!customer) return null;

  try {
    // Use updatedAt as proxy for activity
    // Detail view can calculate from actual leads/quotes/invoices
    if (customer.updatedAt) {
      return new Date(customer.updatedAt);
    }
    if (customer.createdAt) {
      return new Date(customer.createdAt);
    }
    return null;
  } catch (error) {
    logError("Failed to calculate last activity date", {
      customerId: customer.customerId,
      error: error.message,
    });
    return null;
  }
};

/**
 * Determine if customer is active (activity in last 30 days)
 * @param {Date|null} lastActivityDate - Last activity date
 * @returns {boolean} True if active
 */
export const isCustomerActive = (lastActivityDate) => {
  if (!lastActivityDate) return false;

  try {
    const daysSinceActivity =
      (Date.now() - new Date(lastActivityDate).getTime()) /
      (1000 * 60 * 60 * 24);

    return daysSinceActivity <= 30;
  } catch (error) {
    logError("Failed to determine customer activity status", {
      lastActivityDate,
      error: error.message,
    });
    return false;
  }
};

/**
 * Calculate activity count (leads + quotes combined)
 * @param {Object} customer - Customer object
 * @returns {number} Total activity count
 */
export const getActivityCount = (customer) => {
  if (!customer) return 0;
  const leadCount = customer.leadIds?.length || 0;
  const quoteCount = customer.quoteIds?.length || 0;
  return leadCount + quoteCount;
};

/**
 * Format phone number for display
 * Removes +27 prefix and handles TEMP_ placeholders
 * @param {string} phone - Phone number
 * @returns {string|null} Formatted phone or null
 */
export const formatPhoneDisplay = (phone) => {
  if (!phone || phone.startsWith("TEMP_")) return null;
  return phone.replace("+27", "0");
};

/**
 * Format last active date for display
 * Returns "Today", "Yesterday", "X days ago", or formatted date
 * @param {Date|string|null} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatLastActive = (date) => {
  if (!date) return "Never";

  try {
    const dateObj = new Date(date);
    const now = Date.now();
    const diffMs = now - dateObj.getTime();
    const daysDiff = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (daysDiff === 0) return "Today";
    if (daysDiff === 1) return "Yesterday";
    if (daysDiff < 7) return `${daysDiff} days ago`;
    if (daysDiff < 30) return format(dateObj, "MMM dd");
    return format(dateObj, "MMM dd, yyyy");
  } catch (error) {
    logError("Failed to format last active date", {
      date,
      error: error.message,
    });
    return "Unknown";
  }
};

/**
 * Filter and enhance customers with quality checks and activity data
 * Filters out low-quality customers, adds status and activity metadata, and sorts
 * @param {Array} customers - Raw customer array
 * @returns {Array} Filtered and enhanced customers
 */
export const enhanceCustomers = (customers) => {
  if (!Array.isArray(customers)) {
    logError("enhanceCustomers received non-array", { customers });
    return [];
  }

  try {
    return customers
      .filter((customer) => !isLowQualityCustomer(customer))
      .map((customer) => {
        const lastActivityDate = calculateLastActivityDate(customer);
        const isActive = isCustomerActive(lastActivityDate);

        return {
          ...customer,
          lastActivityDate,
          status: isActive ? "active" : "dormant",
          activityCount: getActivityCount(customer),
        };
      })
      .sort((a, b) => {
        // Sort by: active first, then by last activity date (newest first)
        if (a.status !== b.status) {
          return a.status === "active" ? -1 : 1;
        }
        if (a.lastActivityDate && b.lastActivityDate) {
          return (
            new Date(b.lastActivityDate).getTime() -
            new Date(a.lastActivityDate).getTime()
          );
        }
        if (a.lastActivityDate && !b.lastActivityDate) return -1;
        if (!a.lastActivityDate && b.lastActivityDate) return 1;
        return 0;
      });
  } catch (error) {
    logError("Failed to enhance customers", {
      error: error.message,
      stack: error.stack,
    });
    return [];
  }
};

/**
 * Detect duplicate customers by phone number
 * Groups customers with the same phone number for merge functionality
 * @param {Array} customers - Customer array
 * @returns {Array} Array of duplicate groups (each group has 2+ customers)
 */
export const detectDuplicates = (customers) => {
  if (!Array.isArray(customers)) return [];

  try {
    const phoneMap = new Map();

    customers.forEach((customer) => {
      const phone = formatPhoneDisplay(customer.phone);
      if (phone) {
        if (!phoneMap.has(phone)) {
          phoneMap.set(phone, []);
        }
        phoneMap.get(phone).push(customer);
      }
    });

    return Array.from(phoneMap.values()).filter((group) => group.length > 1);
  } catch (error) {
    logError("Failed to detect duplicates", {
      error: error.message,
    });
    return [];
  }
};

/**
 * Calculate last activity date from actual customer relations
 * More accurate than using updatedAt - checks leads, quotes, invoices, orders
 * @param {Object} customer - Customer object with relations
 * @returns {Date|null} Last activity date
 */
export const calculateLastActivityDateFromRelations = (customer) => {
  if (!customer) return null;

  try {
    const dates = [];

    // Check leads
    if (customer.leads && Array.isArray(customer.leads)) {
      customer.leads.forEach((lead) => {
        if (lead.updatedAt) dates.push(new Date(lead.updatedAt));
        if (lead.createdAt) dates.push(new Date(lead.createdAt));
      });
    }

    // Check quotes
    if (customer.quotes && Array.isArray(customer.quotes)) {
      customer.quotes.forEach((quote) => {
        if (quote.updatedAt) dates.push(new Date(quote.updatedAt));
        if (quote.createdAt) dates.push(new Date(quote.createdAt));
      });
    }

    // Check invoices
    if (customer.invoices && Array.isArray(customer.invoices)) {
      customer.invoices.forEach((invoice) => {
        if (invoice.updatedAt) dates.push(new Date(invoice.updatedAt));
        if (invoice.createdAt) dates.push(new Date(invoice.createdAt));
      });
    }

    // Check orders
    if (customer.orders && Array.isArray(customer.orders)) {
      customer.orders.forEach((order) => {
        if (order.updatedAt) dates.push(new Date(order.updatedAt));
        if (order.createdAt) dates.push(new Date(order.createdAt));
      });
    }

    // Check bookings
    if (customer.bookings && Array.isArray(customer.bookings)) {
      customer.bookings.forEach((booking) => {
        if (booking.updatedAt) dates.push(new Date(booking.updatedAt));
        if (booking.createdAt) dates.push(new Date(booking.createdAt));
      });
    }

    // Return most recent date
    if (dates.length > 0) {
      return new Date(Math.max(...dates.map((d) => d.getTime())));
    }

    // Fallback to customer updatedAt
    if (customer.updatedAt) {
      return new Date(customer.updatedAt);
    }

    if (customer.createdAt) {
      return new Date(customer.createdAt);
    }

    return null;
  } catch (error) {
    logError("Failed to calculate last activity date from relations", {
      customerId: customer.customerId,
      error: error.message,
    });
    return null;
  }
};

/**
 * Detect customer risk factors
 * Checks for overdue invoices, complaints in notes, disputes
 * @param {Object} customer - Customer object with relations
 * @param {Array} notes - Customer notes array
 * @returns {boolean} True if customer has risk factors
 */
export const detectCustomerRisk = (customer, notes = []) => {
  if (!customer) return false;

  try {
    // Check for overdue invoices
    if (customer.invoices && Array.isArray(customer.invoices)) {
      const overdueInvoices = customer.invoices.filter((inv) => {
        if (inv.status === "overdue") return true;
        if (inv.dueDate) {
          const dueDate = new Date(inv.dueDate);
          const now = new Date();
          return (
            dueDate < now && inv.status !== "paid" && inv.status !== "cancelled"
          );
        }
        return false;
      });

      if (overdueInvoices.length > 0) return true;
    }

    // Check for complaints or disputes in notes
    if (Array.isArray(notes) && notes.length > 0) {
      const complaintKeywords = [
        "complaint",
        "dispute",
        "unhappy",
        "dissatisfied",
        "refund",
        "cancel",
        "problem",
        "issue",
        "wrong",
        "error",
      ];

      const hasComplaint = notes.some((note) => {
        const content = (note.content || "").toLowerCase();
        return complaintKeywords.some((keyword) => content.includes(keyword));
      });

      if (hasComplaint) return true;
    }

    return false;
  } catch (error) {
    logError("Failed to detect customer risk", {
      customerId: customer.customerId,
      error: error.message,
    });
    return false;
  }
};

/**
 * Determine customer health status
 * Returns 'active', 'dormant', or 'risk'
 * @param {Object} customer - Customer object with relations
 * @param {Array} notes - Customer notes array
 * @returns {string} Health status
 */
export const getCustomerHealth = (customer, notes = []) => {
  if (!customer) return "dormant";

  try {
    // Check for risk factors first
    if (detectCustomerRisk(customer, notes)) {
      return "risk";
    }

    // Calculate activity date from relations if available
    const lastActivityDate =
      customer.leads || customer.quotes || customer.invoices
        ? calculateLastActivityDateFromRelations(customer)
        : calculateLastActivityDate(customer);

    // Determine if active
    const isActive = isCustomerActive(lastActivityDate);

    return isActive ? "active" : "dormant";
  } catch (error) {
    logError("Failed to determine customer health", {
      customerId: customer.customerId,
      error: error.message,
    });
    return "dormant";
  }
};
